import cn from 'classnames';
import * as React from 'react';

import styles from './button.module.css';

export interface ButtonProps
  extends React.DetailedHTMLProps<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
  > {
  size?: 'lg' | 'sm';
  className?: string;
  disabled?: boolean;
  status?:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'danger'
    | 'ghost'
    | 'rounded'
    | 'secondary-gray'
    | 'tertiary-gray'
    | 'tertiary-green';
  children?: React.ReactNode;
  iconRight?: JSX.Element | undefined;
  iconLeft?: JSX.Element | undefined;
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

export const Button = React.memo(
  ({
    size = 'sm',
    className,
    disabled,
    status = 'primary',
    children,
    iconRight,
    iconLeft,
    onClick,
    ...otherProps
  }: ButtonProps): JSX.Element => {
    const isSecondary = status === 'secondary';
    const isPrimary = status === 'primary';
    const isSizeLg = size === 'lg';
    const isSizeSm = size === 'sm';
    const isRounded = status === 'rounded';
    const isGhost = status === 'ghost';
    const isDanger = status === 'danger';
    const isTertiary = status === 'tertiary';
    const isSecondaryGray = status === 'secondary-gray';
    const isTertiaryGray = status === 'tertiary-gray';
    const isTertiaryGreen = status === 'tertiary-green';
    const calcDisabled =
      isPrimary ||
      isSecondary ||
      isSecondary ||
      isSecondaryGray ||
      isTertiary ||
      isDanger ||
      isTertiaryGreen ||
      isTertiaryGray;

    const btnClassName = cn({
      [className || '']: !!className,
      [styles.button]: true,

      // Primary Styles
      [`${styles.primary} ${styles['spacing-lg']}`]: isPrimary && !disabled && isSizeLg,
      [`${styles.primary} ${styles['spacing-sm']}`]: isPrimary && !disabled && isSizeSm,

      // Secondary Styles
      [`${styles.secondary} ${styles['spacing-lg']}`]: isSecondary && !disabled && isSizeLg,
      [`${styles.secondary} ${styles['spacing-sm']}`]: isSecondary && !disabled && isSizeSm,

      // Secondary Gray Styles
      [`${styles['secondary-gray']} ${styles['spacing-lg']}`]:
        isSecondaryGray && !disabled && isSizeLg,
      [`${styles['secondary-gray']} ${styles['spacing-sm']}`]:
        isSecondaryGray && !disabled && isSizeSm,

      // Tertiary Primary Styles
      [`${styles.tertiary} ${styles['tertiary-spacing']}`]:
        isTertiary && !disabled && (isSizeLg || isSizeSm),

      // Tertiary Gray Styles
      [`${styles['tertiary-gray']} ${styles['tertiary-spacing']}`]:
        isTertiaryGray && !disabled && (isSizeLg || isSizeSm),

      // Tertiary Green Styles
      [`${styles['tertiary-green']} ${styles['tertiary-spacing']}`]:
        isTertiaryGreen && !disabled && (isSizeLg || isSizeSm),

      //  Common Disabled Styles
      [`${styles['disabled-btn']} ${styles['spacing-lg']}`]: calcDisabled && disabled && isSizeLg,
      [`${styles['disabled-btn']} ${styles['spacing-sm']}`]: calcDisabled && disabled && isSizeSm,

      // Danger Styles
      [`${styles.danger} ${styles['spacing-lg']}`]: isDanger && !disabled && isSizeLg,
      [`${styles.danger} ${styles['spacing-sm']}`]: isDanger && !disabled && isSizeSm,

      // Ghost Styles
      [`${styles.ghost}`]: isGhost && !disabled && (isSizeLg || isSizeSm),
      [`${styles['ghost-disabled']}`]: isGhost && disabled && (isSizeLg || isSizeSm),

      // Rounded Styles
      [`${styles['rounded-btn']}`]: isRounded && !disabled && isSizeSm,
      [`${styles['rounded-btn']} ${styles['spacing-lg']}`]: isRounded && !disabled && isSizeLg,
    });

    return (
      <button
        onClick={onClick}
        className={btnClassName}
        type="button"
        aria-label="Buttton Component"
        {...otherProps}
      >
        {iconLeft ? <span className="pr-6 flex">{iconLeft}</span> : null}
        {children}
        {iconRight ? <span className="pl-13 flex ml-auto">{iconRight}</span> : null}
      </button>
    );
  }
);

export const TextButton = ({
  children,
  className,
  style,
}: {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}): JSX.Element => {
  const classes = cn({
    'inline-flex items-center text-primary-500 text-sm active:text-primary-800 focus:outline-none font-medium hover:text-primary-700':
      true,
    [className || '']: !!className,
  });
  return (
    <button className={classes} style={{ ...style }}>
      {children}
    </button>
  );
};

export interface IconTextBtnProps
  extends React.DetailedHTMLProps<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
  > {
  children?: React.ReactNode;
  iconRight?: JSX.Element | undefined;
  iconLeft?: JSX.Element | undefined;
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  label?: string;
  color?: 'gray';
}

export const IconTextBtn = ({
  children,
  iconLeft,
  iconRight,
  onClick,
  className,
  label,
  color,
  ...otherProps
}: IconTextBtnProps): JSX.Element => {
  const btnClassName = cn({
    'flex items-center text-sm focus:shadow-outline': true,
    [className || '']: !!className,
    'text-gray-500 hover:bg-gray-100 rounded-sm active:bg-gray-200': color === 'gray',
  });
  return (
    <button
      onClick={onClick}
      className={btnClassName}
      type="button"
      aria-label={label}
      title={label}
      {...otherProps}
    >
      {iconLeft ? <span className="flex">{iconLeft}</span> : null}
      {children}
      {iconRight ? <span className="flex">{iconRight}</span> : null}
    </button>
  );
};
